import { EntityType, UploadType } from "@redux/common/reduxTypes"

import { SubResourceUrlPattern, IRIstub, IModel, IRI } from "./schema"
import { ChallengeType } from "./schema/challenges"

/**
 * type of an endpoint that is implemented as sub resource of a entity endpoint by the backend API
 */
interface SubResourceEndpoint {
  /**
   * URL of the sub endpoint, e.g. "/projects/[id]/memberships".
   * This allows also completely different endpoint targets, different from the url of the base entity.
   */
  url: SubResourceUrlPattern
  /**
   * The API omits (mostly/always?) the property that contains the parent entity IRI.
   * To have full information this property name is deposited here.
   *
   * Example: UserObjectRoles fetched from the users sub resource endpoint are missing the "user" prop.
   * So "user" should be put here.
   *
   * NOTE: this is for information purposes only (24.04.2024)
   */
  nameOfOmittedParentProperty?: string
}


/**
 * type of an array of sub resources under a parent entity point
 */
type SubResourceList = {
  [key in EntityType]?: SubResourceEndpoint
}

/**
 * this data defines an endpoint
 */
interface Endpoint {
  /**
   * Url of an endpoint also matches the IRI stub of an entity.
   * Url must not end with a slash.
   */
  url: IRIstub
  /**
   * context string: what the API delivers as collection "@context"
   */
  "@context"?: string
  /**
   * type string: what the API delivers as collection "@type"
   */
  "@type"?: string
  /**
   * optional sub resources that may be fetched from sub-urls of this endpoint
   * or used to trigger actions
   */
  subResource?: SubResourceList
  /**
   * If the EntityType corresponds to a subclass of an (abstract) super class in the backend
   * that has additional endpoints, a corresponding EntityType for this super class is required
   * and must be referenced here.
   * This implements the class-mapping strategy "explicated hierarchy" (see doc/principles/data-schema.md).
   */
  superEntityType: EntityType | undefined
  /**
   * List of all IRIstubs of subclasses of this entity.
   * If the (backend) class hierarchy is not completely modeled with (frontend) EntityTypes,
   * this prop aids in identifying the (base class) EntityType of any entity that is
   * without (frontend) EntityType, using the entity's IRI.
   * This implements the class-mapping strategy "merged hierarchy" (see doc/principles/data-schema.md).
   * // TODO challenge Tests bauen, die:
   * (a) testen, dass keine subclass 2mal vorkommt
   * (b) testen, dass alle bekannten Subklassen vorkommen (im Test durchnummerieren)
   * (c) testen, dass keine IRI als Subklasse vorkommt, die einen eigenen EntityType-Eintrag hat
   * (d) ggf noch mehr?
   */
  irisOfSubclassesWithoutSubEntityType?: IRIstub[]
}

/**
 * an endpoint for a specific entity is defined as
 * EntityType => Endpoint
 */
type EntityEndpointList = {
  [key in EntityType]: Endpoint
}

/**
 * an endpoint for a upload is defined as
 * UploadType => Endpoint
 */
type UploadEndpointList = {
  [key in UploadType]: Omit<Endpoint, Partial<"superEntityType">>
}

// #endregion

// #region endpoint definitions

/**
 * This list contains all entity-specific endpoints
 * to be used in the generic functions to create, update, delete, load single elementes and collections
 * of an entity.
 *
 * Every url is identical with the central part of an "@id" of an IModel, called IRI.
 * So with this list it is also possible to calculate the EntityType from an IRI, done in
 * entitytypeFromIri()
 */
export const entityEndpointList: EntityEndpointList = {

  // #region Challenges + Proposals
  [EntityType.AbstractChallenge]: {
    url: "/challenges",
    "@context": "/contexts/Challenge",
    superEntityType: undefined,
    subResource: {
      [EntityType.Program]: {
        url: "/challenges/[id]/related_programs"
      },
    }
  },
  [EntityType.BasicChallenge]: {
    url: "/basic_challenges",
    "@context": "/contexts/BasicChallenge",
    "@type": ChallengeType.BasicChallenge,
    superEntityType: EntityType.AbstractChallenge,
    subResource: {
      [EntityType.AttachmentDefinition]: {
        url: "/basic_challenges/[id]/attachment_definitions"
      },
      [EntityType.BasicProposal]: {
        url: "/basic_challenges/[id]/proposals"
      },
      [EntityType.UserObjectRole]: {
        url: "/basic_challenges/[id]/managers"
      }
    }
  },
  [EntityType.Fund]: {
    url: "/funds",
    "@context": "/contexts/Fund",
    "@type": ChallengeType.Fund,
    superEntityType: EntityType.AbstractChallenge,
    subResource: {
      [EntityType.AttachmentDefinition]: {
        url: "/funds/[id]/attachment_definitions"
      },
      [EntityType.FundApplication]: {
        url: "/funds/[id]/proposals"
      },
      [EntityType.UserObjectRole]: {
        url: "/funds/[id]/managers"
      }
    }
  },

  [EntityType.AbstractProposal]: {
    url: "/proposals",
    "@context": "/contexts/Proposal",
    "@type": "Proposal",
    superEntityType: undefined,
  },
  [EntityType.BasicProposal]: {
    url: "/basic_proposals",
    "@context": "/contexts/BasicProposal",
    "@type": "BasicProposal",
    superEntityType: EntityType.AbstractProposal,
    subResource: {
      [EntityType.AbstractProposalAttachment]: {
        url: "/basic_proposals/[id]/attachments"
      }
    }
  },
  [EntityType.FundApplication]: {
    url: "/fund_applications",
    "@context": "/contexts/FundApplication",
    "@type": "FundApplication",
    superEntityType: EntityType.AbstractProposal,
    subResource: {
      [EntityType.AbstractProposalAttachment]: {
        url: "/fund_applications/[id]/attachments"
      }
    }
  },

  [EntityType.AttachmentDefinition]: {
    url: "/attachment_definitions",
    "@context": "/contexts/AttachmentDefinition",
    "@type": "AttachmentDefinition",
    superEntityType: undefined,
  },

  [EntityType.AbstractProposalAttachment]: {
    url: "/proposal_attachments",
    "@context": "/contexts/ProposalAttachment",
    "@type": "ProposalAttachment",
    superEntityType: undefined,
  },
  [EntityType.DocumentProposalAttachment]: {
    url: "/document_proposal_attachments",
    "@context": "/contexts/DocumentProposalAttachment",
    "@type": "DocumentProposalAttachment",
    superEntityType: EntityType.AbstractProposalAttachment,
  },
  [EntityType.FileProposalAttachment]: {
    url: "/file_proposal_attachments",
    "@context": "/contexts/FileProposalAttachment",
    "@type": "FileProposalAttachment",
    superEntityType: EntityType.AbstractProposalAttachment,
  },
  [EntityType.ProjectPlanProposalAttachment]: {
    url: "/project_plan_proposal_attachments",
    "@context": "/contexts/ProjectPlanProposalAttachment",
    "@type": "ProjectPlanProposalAttachment",
    superEntityType: EntityType.AbstractProposalAttachment,
  },
  // #endregion

  // #region Documents & Chapters, FinancePlan
  [EntityType.ProjectDocument]: {
    // NOTE: documents currently are only available for Projects
    url: "/project_documents",
    "@context": "/contexts/Document",
    superEntityType: undefined,
    subResource: {
      [EntityType.Chapter]: {
        url: "/project_documents/[id]/chapters"
      }
      // another sub-resource: "/project_documents/[id]/create-pdf"
    }
  },
  [EntityType.Chapter]: {
    url: "/chapters",
    "@context": "/contexts/Chapter",
    superEntityType: undefined
    // no get for single & collections
    // only patch + delete
  },

  [EntityType.FinancePlan]: {
    url: "/finance_plans",
    "@context": "/contexts/FinancePlan",
    superEntityType: undefined
  },
  // #endregion

  // #region Feedback + Discussion
  [EntityType.Discussion]: {
    url: "/discussions",
    "@context": "/contexts/Discussion",
    "@type": "Discussion",
    superEntityType: undefined,
    subResource: {
      [EntityType.FeedbackPost]: {
        url: "/discussions/[id]/posts"
      }
    }
  },
  [EntityType.FeedbackInvitation]: {
    url: "/feedback_invitations",
    "@context": "/contexts/FeedbackInvitation",
    "@type": "FeedbackInvitation",
    superEntityType: undefined,
  },
  [EntityType.FeedbackPost]: {
    // @todo multi: an diesem Endpunkt können keine Entities als Collection gefetched werden!
    // Das ist eine reine IRI-Angabe!
    // https://futureprojects.atlassian.net/browse/FCP-1768
    // Aber das Fetching von Einzelposts ist möglich an /feedback_posts/[id]
    url: "/feedback_posts",
    "@context": "/contexts/FeedbackPost",
    "@type": "FeedbackPost",
    superEntityType: undefined,
    // @todo: Dieser Subresource-Endpunkt existiert im Backend, wird aber im Client hartcodiert in der
    // replyFeedbackPost(). Rework des entityEndpointList-Konzepts nötig.
    // subResource: {
    //   // reply to an existing feedbackpost
    //   [EntityType.FeedbackPost]: {
    //     url: "/feedback_posts/[id]/reply"
    //   }
    // }
  },
  // #endregion

  // #region Other EntityTypes
  [EntityType.Category]: {
    // this url is only used, when a category needs to be updated or deleted
    url: "/categories",
    "@context": "/contexts/Category",
    "@type": "Category",
    superEntityType: undefined,
  },
  [EntityType.Idea]: {
    url: "/ideas",
    "@context": "/contexts/Idea",
    "@type": "Idea",
    superEntityType: undefined,
  },
  [EntityType.Program]: {
    url: "/programs",
    "@context": "/contexts/Program",
    "@type": "Program",
    superEntityType: undefined,
    subResource: {
      [EntityType.Category]: {
        // used to create a category or get all categories of a program
        url: "/programs/[id]/categories"
      },
      [EntityType.AbstractChallenge]: {
        url: "/programs/[id]/related_challenges"
      },
      [EntityType.Idea]: {
        url: "/programs/[id]/ideas",
      },
      [EntityType.Project]: {
        url: "/programs/[id]/projects"
      },
      [EntityType.UserObjectRole]: {
        url: "/programs/[id]/managers",
        nameOfOmittedParentProperty: "program"
      },
    }
  },
  [EntityType.Project]: {
    url: "/projects",
    "@context": "/contexts/Project",
    "@type": "Project",
    superEntityType: undefined,
    subResource: {
      [EntityType.ActionRequest]: {
        url: "/projects/[id]/action_requests"
      },
      [EntityType.Discussion]: {
        url: "/projects/[id]/discussions"
      },
      [EntityType.ProjectDocument]: {
        url: "/projects/[id]/documents"
      },
      [EntityType.FinancePlan]: {
        url: "/projects/[id]/finance_plans"
      },
      [EntityType.AbstractProposal]: {
        url: "/projects/[id]/proposals"
      },
      // create a team upload and get collection of team uploads
      [EntityType.TeamUpload]: {
        url: "/projects/[id]/team_uploads",
      },
      [EntityType.UserObjectRole]: {
        url: "/projects/[id]/memberships"
      },

      // this endpoint does not deliver followerships but is necessary to CREATE a followership for the currently logged-in user
      [EntityType.ProjectFollowership]: {
        url: "/projects/[id]/follow",
      },
    }
  },
  // this endpoint does not deliver followerships but is necessary to DELETE a followership
  [EntityType.ProjectFollowership]: {
    url: "/project_followerships",

    "@context": "/contexts/ProjectFollowership",
    "@type": "ProjectFollowership",
    superEntityType: undefined,
  },
  [EntityType.Provider]: {
    url: "/providers",
    "@context": "/contexts/Provider",
    "@type": "Provider",
    superEntityType: undefined,
    subResource: {
      [EntityType.Program]: {
        url: "/providers/[id]/programs",
      },
      [EntityType.AbstractChallenge]: {
        url: "/providers/[id]/challenges"
      },
      [EntityType.UserObjectRole]: {
        url: "/providers/[id]/managers",
      },
    }
  },
  [EntityType.SupportRequest]: {
    url: "/support_requests",
    "@context": "/contexts/SupportRequest",
    "@type": "SupportRequest",
    superEntityType: undefined,
  },
  // only for edit and delete a team upload
  [EntityType.TeamUpload]: {
    url: "/team_uploads",
    "@context": "/contexts/TeamUpload",
    "@type": "Teamupload",
    superEntityType: undefined,
  },
  [EntityType.User]: {
    url: "/users",
    "@context": "/contexts/User",
    "@type": "User",
    superEntityType: undefined,
    subResource: {
      // Get all action requests, where the user is receiver or sender (e.g. project member applications)
      [EntityType.ActionRequest]: {
        url: "/users/[id]/action_requests"
      },
      // the discussions of the user, created by himself
      // @todo: es gibt einen zweiten Endpunkt, der Discussions liefert: /users/[id]/discussions:
      // die Diskussions ÜBER den User <- die entityEndpointList erlaubt es aber nicht, mehrere solcher Typen anzugeben
      // https://futureprojects.atlassian.net/browse/FCP-1768
      [EntityType.Discussion]: {
        url: "/users/[id]/created-discussions"
      },
      [EntityType.UserObjectRole]: {
        url: "/users/[id]/object_roles",
        nameOfOmittedParentProperty: "user"
      },
      [EntityType.Idea]: {
        url: "/users/[id]/ideas",
      },
      [EntityType.Project]: {
        url: "/users/[id]/projects",
      },
      [EntityType.ProjectFollowership]: {
        url: "/users/[id]/followerships",
      },
    }
  },
  [EntityType.UserObjectRole]: {
    // @todo multi: or set this to NULL, since it is neither a true endpoint nor an entity IRI prefix? (same for Category)
    // This should be probably completely deleted, because it is a subressource. Therefor the EntityEndpointList has to be redefined:
    // export type EntityEndpointList = {
    //  [key in Exclude<EntityType, EntityType.UserObjectRole>]: Endpoint
    // }
    url: "/object_roles",
    "@context": "/contexts/UserObjectRole",
    "@type": "UserObjectRole",
    superEntityType: undefined,
    irisOfSubclassesWithoutSubEntityType: [
      "/account_managers",
      "/challenge_managers",
      "/community_managers",
      "/project_coordinators",
      "/project_observers",
      "/project_planners",
      "/tenant_managers",
    ]
  },
  [EntityType.ActionRequest]: {
    // @todo multi: or set this to NULL, since it is neither a true endpoint nor an entity IRI prefix? (same for Category)
    url: "/action_requests",
    "@context": "/contexts/ActionRequest",
    "@type": "ActionRequest",
    superEntityType: undefined,
    irisOfSubclassesWithoutSubEntityType: [
      "/abuse_reports",
      "/account_manager_invitations",
      "/activate_tenant_requests",
      "/bug_reports",
      "/community_manager_invitations",
      "/challenge_manager_invitations",
      "/lock_user_requests",
      "/project_member_applications",
      "/program_fund_reference_requests",
      "/project_coordinator_invitations",
      "/project_observer_invitations",
      "/project_planner_invitations",
      "/tenant_manager_invitations",
      "/user_messages",
    ]
  }
  // #endregion
}

/**
 * List of EntityTypes that have an "/statistics" sub endpoint,
 * e.g. /programs/[id]/statistics
 */
export const availableStatisticsEndpointsForEntityType: EntityType[] =
  [
    EntityType.Program,
  ] as const

/**
 * This list contains most upload-specific endpoints
 * to be used in the generic functions to upload (create), update or delete a file.
 *
 * Because a file is usually bound to a specific entity that is identified by its id
 * the URL usually uses [id] as placeholder to be replaced by the real id by the upload function.
 */
export const uploadEndpointList: UploadEndpointList = {
  [UploadType.BasicChallengeLogo]: {
    url: "/basic_challenges/[id]/logo",
  },
  [UploadType.FundLogo]: {
    url: "/funds/[id]/logo",
  },
  [UploadType.ProgramLogo]: {
    url: "/programs/[id]/logo",
  },
  [UploadType.ProjectPicture]: {
    url: "/projects/[id]/picture",
  },
  [UploadType.ProjectVisualization]: {
    url: "/projects/[id]/visualization",
  },
  [UploadType.UserPicture]: {
    url: "/users/[id]/picture",
  }
}

// #region endpointList helper


/**
 * Returns the corresponding EntityType on a given IRI/"@id".
 *
 * NOTE that E is optional. Coders may provide a restricted / sub type of EntityType as defined in /src/redux/reduxTypes.ts.
 * Providing E has no effect on the internals of this method, since generic type parameters are not available at runtime.
 * Hence it's not possible to ensure that the given IRI is in the domain of E.
 * However, providing E allows coders to statically type the return type of this method, allowing them to call
 * further methods that only work on a restricted / sub type of EntityType.
 *
 * @todo find a way to ensure IRIs match the EntityType (FCP-1260)
 *
 * @param iri a valid IRI (pattern: /entity_endpoint_url/8) from an IModel[@id] to identify a single entity
 * @returns the corresponding EntityType
 */
const entityTypeFromIRI = <E extends EntityType>(iri: IRI): E => {
  if (!iri) {
    return null
  }

  // first character must be a /
  if (!iri.startsWith("/")) {
    return null
  }

  const indexOfSecondSlash = iri.indexOf("/", 2) // start searching on position after first character
  // no second slash found? -> no IRI!
  if (indexOfSecondSlash === -1) {
    return null
  }

  // calculate the isolated URL
  const urlFromIri = iri.substring(0, indexOfSecondSlash) as IRIstub

  // find the key of the corresponding entry in the entityEndpointList -> this is the searched EntityType-value!
  let entityTypeKey: string
  Object.entries(entityEndpointList).forEach(([key, endpoint]) => {
    // Matches the given URL with the current endpoint's URL?
    // -> found the correct entry
    if (endpoint.url === urlFromIri) {
      entityTypeKey = key

      return
    }

    // Or is the given URL listed in the current endpoint's list of "SubclassesWithoutSubEntityType"?
    // -> found the correct entry
    if (endpoint.irisOfSubclassesWithoutSubEntityType?.includes(urlFromIri)) {
      entityTypeKey = key

      return
    }
  })
  return entityTypeKey as E
}

/**
 * Returns the EntityType of a given IModel
 *
 * {@link entityTypeFromIRI} for notes on type safety.
 *
 * @param entity an IModel with valid "@id" or a plain "@id"/IRI
 * @returns the EntityType of the given entity
 */
export const entityTypeFromIModelOrIRI = <E extends EntityType>(entity: IModel | IRI): E => {
  // if no entity is given
  if (!entity) {
    return null
  }

  if (typeof entity === "string") {
    return entityTypeFromIRI<E>(entity)
  } else {
    if (entity["@id"]) {
      return entityTypeFromIRI<E>(entity["@id"])
    }
    else {
      // if there is no "@id"
      return undefined
    }
  }
}

/**
 * Returns the IRI ("@id") for an IModel of given EntityType with the given id.
 *
 * @param entityType The type of the IModel
 * @param id The id of the IModel
 * @returns the iri/"@id" as string
 *
 * @deprecated EntityTypes may have multiple IRI prefixes - find a more specific solution (it's only used with currentUser, anyhow)
 */
export const iriFromEntityTypeAndId = (entityType: EntityType, id: number): IRI =>
  // @todo multi: es gibt entity typen, deren IRI sich von der URL in der entityEndpointList unterscheidet, e.g. UserObjectRoles
  `${entityEndpointList[entityType].url}/${id}`

// #endregion

// #region helper on IDs or IRIs calculation/extraction of IModel types

/**
 * Returns the IRI ("@id") of an IModel or a string depending on the type of the given parameter.
 * Should be used when an entity property is of type "string | IModel" to have a shortcut to get the
 * IRI. If the type is string it is expected that it contains the IRI ('@id').
 *
 * @param iriElement represents an IRI or an IModel
 * @returns the iriElement as string if it is a string (which should be also an @id) or the "@id" of the IModel
 */
export const iriFromIModelOrIRI = (iriElement: IModel | IRI): IRI => {
  if (typeof iriElement === "string") {
    // calculate iristub by searching for slash after position 1, assuming slash at position 0
    const iristub = iriElement.substring(0, iriElement.indexOf("/", 1))
    // check, if the IRIsub is a known endpoint
    // @todo multi: es gibt entity typen, deren IRI sich von der URL in der entityEndpointList unterscheidet, e.g. UserObjectRoles
    if (Object.values(entityEndpointList).find(iri => iri.url === iristub)) {
      return iriElement
    }
    else {
      // if the IRIstub is not part of known endpoints
      return undefined
    }
  } else {
    return iriElement?.["@id"]
  }
}

/**
 * Returns if a given IModel or IRI correlates to a given EntityType
 *
 * @param entity an IModel with valid "@id" or a "@id"/IRI
 * @param entityType the EntityType the entity should match
 * @returns true, if the given entity has the given EntityType
 */
export const isOfEntityType = (entity: IModel | IRI, entityType: EntityType): boolean => {
  return entityTypeFromIModelOrIRI(entity) === entityType
}

/**
 * Returns the superEntityType of the specified entityType, if one exists.
 *
 * @param entityType
 * @returns superEntityType or null
 */
export const getSuperEntityType = (entityType: EntityType): EntityType => {
  return entityEndpointList[entityType]?.superEntityType
}
// #endregion